// Geometry for the Podwórko (BackyardScene) per upgrade level. Shared by the owner scene
// and VisitBackyardScene so visitors walk exactly where the host does. Pure data — no
// Phaser imports — so it loads under `node --test`.
//
// Coordinates are CANVAS pixels (1280×720). Authored precisely in Scene Edit Mode
// (press E in dev, Tab → walk mode, click the line, S copies it to the clipboard) against
// the real generated art for each level.
//
// No config.js import on purpose: config reads import.meta.env and can't load under node.
import { BACKYARD_WIDE } from './backyardWide.mjs';

const GAME_WIDTH = 1280;
const GAME_HEIGHT = 720;
// Podwórko is 1.5 screens wide since 2026-10-22 (spec 2026-10-22-podworko-in-garden-design.md): the
// hedge, grill and altana stayed where they were; the garage block (garage, driveway, sapling — at lvl 1
// the barrel, logs and sapling) moved S px right, and the gap holds the back fence's gate and a path.
const S = BACKYARD_WIDE.shift;   // 640
export const BACKYARD_WORLD_W = 1920;
export const BACKYARD_BG_PLACEMENT = { x: BACKYARD_WORLD_W / 2, y: GAME_HEIGHT / 2, w: BACKYARD_WORLD_W, h: GAME_HEIGHT };
const R = BACKYARD_WORLD_W - 1;   // the floor's last point

// Walkable floor: feet can't go above this line. Lawn everywhere at lvl 1; from lvl 2 the
// line dips in front of the buildings (grill, altana, garage — all roughly x345–1180 by
// lvl 4/5) so you stay on the lawn / driveway and never "climb" the walls, then rises back
// to the fence line from x1200 to the right edge (lvl 2–4; lvl 5's garage fills that span too).
// The open lawn reaches the fence line (feet y ≈ 380 on every level's art). Everything
// painted into the background pushes the line down: the hedge along the left (its base runs
// from y ≈ 620 at the left edge up to the fence at x ≈ 425 — traced on the lvl-1 art
// 2026-09-06, same hedge on every level), the lvl-1 barrel/logs/sapling on the right, and the
// front edges of the garage (lvl 2+), the altana deck (lvl 3+) and the grill (lvl 4+).
export const BACKYARD_FENCE_FEET_Y = 380;
// Feet may not go above the hedge's base (+6 px so she stands in front of it, not in it).
const HEDGE = [[1, 626], [60, 576], [120, 512], [200, 508], [240, 486], [280, 458], [320, 434], [360, 410], [400, 392], [425, 380]];
const HEDGE_TO_GRILL = HEDGE.slice(0, 7);   // …up to x 320; the lvl 4/5 grill takes over from x 350
export const BACKYARD_FLOOR_BY_LEVEL = {
  1: [...HEDGE, [R, 380]],   // barrel / logs / sapling: walk behind them (see obstacles + foreground)
  2: [...HEDGE, [830 + S, 380], [860 + S, 470], [1235 + S, 470], [1250 + S, 380], [R, 380]],
  3: [...HEDGE, [540, 380], [565, 465], [860, 465], [875, 380], [830 + S, 380], [860 + S, 465], [1235 + S, 465], [1250 + S, 380], [R, 380]],
  4: [...HEDGE_TO_GRILL, [350, 450], [835, 450], [850, 380], [830 + S, 380], [860 + S, 450], [1235 + S, 450], [1250 + S, 380], [R, 380]],
  5: [...HEDGE_TO_GRILL, [350, 450], [795, 450], [810, 380], [735 + S, 380], [750 + S, 450], [1240 + S, 450], [1255 + S, 380], [R, 380]],
};

export function floorForLevel(level) {
  return BACKYARD_FLOOR_BY_LEVEL[level] ?? BACKYARD_FLOOR_BY_LEVEL[1];
}

// Things you walk AROUND and BEHIND (like the zagroda's huts): each has a footprint — a
// band of FEET positions at its base you cannot enter — and a foreground polygon (below)
// that re-draws the object over anyone whose feet are above its base line. Measured on the
// generated art 2026-09-07: the barrel and the log pile exist on lvl 1 only (the garage
// replaces them); the sapling stands at x ≈ 1185–1200 on lvl 1–4 and at x ≈ 1223 on lvl 5.
const FOOT_BAND = 24;
const footprint = (x0, x1, baseY) => ({ x0, x1, y0: baseY - FOOT_BAND, y1: baseY });
const BARREL = footprint(1002 + S, 1084 + S, 481);
const LOGS = footprint(1086 + S, 1170 + S, 488);
const SAPLING_A = footprint(1160 + S, 1205 + S, 492);   // trunk + the stones right at its foot, lvl 1–4
const SAPLING_B = footprint(1188 + S, 1228 + S, 492);   // lvl 5 (the tree stands ~27 px further right)
export const BACKYARD_OBSTACLES_BY_LEVEL = {
  1: [BARREL, LOGS, SAPLING_A],
  2: [SAPLING_A], 3: [SAPLING_A], 4: [SAPLING_A],
  5: [SAPLING_B],
};
// Sprite-centre x bounds for the walker: the outdoor sprite is half as wide as the indoor
// art the default bounds are sized for, and the lawn runs to the edge of the picture.
export const BACKYARD_X_BOUNDS = [50, 1245 + S];
export function backyardObstaclesForLevel(level) {
  return BACKYARD_OBSTACLES_BY_LEVEL[level] ?? BACKYARD_OBSTACLES_BY_LEVEL[1];
}
/** `blocked(feetX, feetY)` for WalkController: true inside any obstacle footprint. */
export function backyardBlocked(level) {
  const rects = backyardObstaclesForLevel(level);
  return (fx, fy) => rects.some((r) => fx >= r.x0 && fx <= r.x1 && fy >= r.y0 && fy <= r.y1);
}

// Foreground polygons (flat [x0,y0,x1,y1,…], world coords) — one per object; the LOWEST
// point of each is its sort line (see sceneForeground.applyForeground), so a canopy has to
// be one polygon with its trunk down to the stones, or it would sort at the leaves' bottom.
// Silhouettes traced per row on the lvl-1 art (2026-09-07): the barrel bulges in the middle
// and rounds off at the rim and the base; the log pile is a rough heap.
// Flat [x, y, …] polygon moved with the garage block.
const shiftX = (flat) => flat.map((v, i) => (i % 2 === 0 ? v + S : v));
const BARREL_POLY = shiftX([1023, 382, 1057, 382, 1071, 388, 1076, 406, 1078, 424, 1078, 442, 1076, 460, 1070, 472, 1050, 481,
  1035, 481, 1012, 474, 1004, 460, 1001, 442, 1001, 424, 1004, 406, 1009, 388]);
const LOGS_POLY = shiftX([1143, 420, 1153, 426, 1157, 438, 1166, 450, 1175, 462, 1175, 488, 1103, 488, 1080, 480, 1080, 450, 1095, 432, 1138, 426]);
const SAPLING_POLY_A = shiftX([1090, 218, 1120, 196, 1130, 180, 1170, 160, 1180, 146, 1200, 168, 1230, 196, 1245, 202, 1245, 296, 1212, 300,
  1192, 310, 1192, 492, 1173, 492, 1173, 310, 1100, 300]);
const SAPLING_POLY_B = shiftX([1150, 232, 1170, 186, 1180, 190, 1210, 168, 1220, 160, 1240, 200, 1260, 196, 1279, 198, 1279, 296, 1250, 300,
  1216, 310, 1216, 492, 1200, 492, 1200, 310, 1160, 300]);
export const BACKYARD_OBJECT_FOREGROUND_BY_LEVEL = {
  1: [BARREL_POLY, LOGS_POLY, SAPLING_POLY_A],
  2: [SAPLING_POLY_A], 3: [SAPLING_POLY_A], 4: [SAPLING_POLY_A],
  5: [SAPLING_POLY_B],
};

// Arriving from the front yard you appear at the hedge gap (bottom-left path). FEET
// coordinates — the scene converts to a sprite centre through its scale/perspective.
export const BACKYARD_SPAWN = { x: 170, feetY: 680 };
// Walking back out lands you near the right edge of the front yard.
export const BACKYARD_EXIT_SPAWN_X = GAME_WIDTH - 200;

// Parked-bike defaults (owner + visit). Lower-left lawn, clear of the path.
export const BACKYARD_BIKE_PARKED_DEFAULTS = {
  pink: { x: 330, y: GAME_HEIGHT - 150 },
  blue: { x: 460, y: GAME_HEIGHT - 150 },
};

// Parked cars. The garage is background art (one door at lvl 2–4, two at lvl 5), so the
// cars stand ON the driveway in front of it — one anchor per garage slot, in the same order
// as `cars.owned`'s garage cars. Each entry is the CENTRE of a 200 px-tall front portrait
// (≈300 px wide, ~10 % of the art below the tyres), so the wheels land ≈80 px lower — on
// the gravel apron (lvl 2–4) / the paving (lvl 5).
// Traced 2026-08-28 by compositing the real sprite onto bg_backyard_day_lvl2..lvl5 scaled to
// the 1280×720 canvas: lvl 2–4 share one garage at x≈860–1150 (door centre ≈980), lvl 5 has
// two doors (centres ≈840 and ≈1010) whose 300 px-wide cars cannot sit side by side, so the
// pair is spread and staggered in y — the right car is nearer the camera and overlaps the
// left one's rear the way two cars on a real driveway do.
export const CAR_ANCHORS_BY_LEVEL = {
  1: [],
  2: [{ x: 970 + S, y: 468 }],
  3: [{ x: 970 + S, y: 468 }],
  4: [{ x: 970 + S, y: 468 }],
  5: [{ x: 800 + S, y: 455 }, { x: 1055 + S, y: 490 }],
};

// A car beyond the last anchor (only reachable through dev-add-car — buying is slot-capped)
// is shunted down and to the right, so it stays visible (and y-sorts in front) instead of
// hiding under the last one.
export const CAR_ANCHOR_SPILL_X = 60;
export const CAR_ANCHOR_SPILL_Y = 16;

export function carAnchorsForLevel(level) {
  const l = Number.isInteger(level) ? Math.min(Math.max(level, 1), 5) : 1;
  return CAR_ANCHORS_BY_LEVEL[l];
}

/** Where the `index`-th garage car stands at this backyard level (null: no garage yet). */
export function carAnchorAt(level, index) {
  const anchors = carAnchorsForLevel(level);
  if (!anchors.length || index < 0) return null;
  const over = index - (anchors.length - 1);
  if (over <= 0) return anchors[index];
  const last = anchors[anchors.length - 1];
  return { x: last.x + over * CAR_ANCHOR_SPILL_X, y: last.y + over * CAR_ANCHOR_SPILL_Y };
}

// Altana (gazebo) seating — from level 3. Four seats in FEET coordinates: 0–1 on the front
// bench (facing the table = away from the viewer, drawn facing right/left toward the
// middle), 2–3 on the back bench. `occluder` is a flat polygon over the front bench + deck
// edge; applyForeground() re-draws that slice of the background ABOVE anyone whose feet are
// higher than its bottom edge. `sitLines` is the y-line where a SEATED body's visible bottom
// rests: seats 0/1 → the front bench's top edge, seats 2/3 → the table-top's top edge (the
// table then reads as hiding everything from the chest down). Seated avatars are cropped to
// head + chest (see SEAT_CROP_FRAC), so the occluder is no longer what hides the legs.
// The altana shifts between levels on the generated art (lvl4 ≈ +30 px, lvl5 ≈ −100 px).
// Authored 2026-08-23 against the real generated art per level (measured in-browser against
// screenshots, then verified by sitting/standing in each seat); sitLines measured the same
// way 2026-08-24. The bench/table edges recede slightly across the altana, so one horizontal
// line per row is a ±3 px fit — close enough that no cut edge reads as floating.
export const ALTANA_BY_LEVEL = {
  3: {
    seats: [{ x: 650, y: 446 }, { x: 770, y: 446 }, { x: 675, y: 422 }, { x: 770, y: 422 }],
    facing: ['right', 'left', 'right', 'left'],
    sitLines: [395, 395, 373, 373],
    occluder: [580, 370, 820, 370, 820, 450, 580, 450],
  },
  4: {
    seats: [{ x: 650, y: 430 }, { x: 760, y: 430 }, { x: 675, y: 406 }, { x: 760, y: 406 }],
    facing: ['right', 'left', 'right', 'left'],
    sitLines: [388, 388, 366, 366],
    occluder: [590, 355, 830, 355, 830, 435, 590, 435],
  },
  5: {
    seats: [{ x: 570, y: 424 }, { x: 690, y: 424 }, { x: 595, y: 400 }, { x: 690, y: 400 }],
    facing: ['right', 'left', 'right', 'left'],
    sitLines: [387, 387, 362, 362],
    occluder: [500, 355, 790, 355, 790, 428, 500, 428],
  },
};

export function altanaForLevel(level) {
  if (!Number.isInteger(level) || level < 3) return null;
  return ALTANA_BY_LEVEL[Math.min(level, 5)];
}

// Container centre for a seated avatar: with SEAT_CROP_FRAC 0.5 the visible bottom
// equals the container y, so the centre sits exactly on the seat's sit line.
export function seatCentre(altana, seat) {
  const s = altana?.seats?.[seat];
  const line = altana?.sitLines?.[seat];
  return s && line != null ? { x: s.x, y: line } : null;
}

// Foreground-key for applyForeground(): one polygon set per level (null below lvl 3).
export function backyardForegroundKey(level) {
  const l = Number.isInteger(level) ? Math.min(Math.max(level, 1), 5) : 1;
  return `backyard_lvl${l}`;
}
// Older name (the altana bench was the only occluder until 2026-09-07).
export const altanaForegroundKey = backyardForegroundKey;

// The gate in the back fence to the garden (spec 2026-10-22), in the painted middle between the altana
// and the moved garage: the same x at every level (scripts/backyard_wide.py GATE_X_SRC 1500 × 0.7656).
// `zone` is in FEET coordinates (walk up the path into the fence there); `arrive` is where you stand
// coming in from the garden, a step below the zone.
export const BACKYARD_GATE = {
  x: 1148,
  zone: { x1: 1108, x2: 1188, y1: 370, y2: 396 },
  label: { x: 1148, y: 300 },
  arrive: { x: 1148, feetY: 440 },
};
// Scene-start data: into Podwórko through its gate (facing down, into the yard)…
export function backyardGateArrival() {
  return { spawnX: BACKYARD_GATE.arrive.x, spawnFeetY: BACKYARD_GATE.arrive.feetY, spawnFacing: 2 };
}
// …and into the garden through its gate (FarmScene / VisitFarmScene stand you next to it, facing up-right).
export function gardenArrivalData() { return { arrive: 'podworko', spawnFacing: 7 }; }
